import {
  LoaderFunctionArgs,
  useFetcher,
  useLoaderData,
  useOutletContext,
  useParams,
} from 'react-router';
import { useBackButton } from 'hooks/useBackButton';
import AuthService from 'utils/AuthService';
import {
  ChangeEvent,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Box, Grid, InputAdornment } from '@mui/material';
import {
  PhoneType,
  Status,
  System,
  Tenant,
  TenantGroup,
  TimeZone,
} from 'types';
import { ValidatorForm } from 'react-material-ui-form-validator';
import TimeZoneSelect from 'components/inputs/TimeZoneSelect';
import { useTranslation } from 'react-i18next';
import { SuperActionHandler } from 'pages/command-center/CommandLayout';
import { ROUTES, routeWithParams } from 'router';
import { DEFAULT_BRANDING } from 'context/themeContext';
import AuturaSwitch from 'components/inputs/AuturaSwitch';
import { ACTION_TYPE, AppContext } from 'context/appContext';
import SettingsSection from 'components/layout/SettingsSection';
import { ScrollableFormContainer } from 'components/layout/ScrollableContainer';
import DetailsHeader from 'components/layout/DetailsHeader';
import AuturaTextField from 'components/inputs/AuturaTextField';
import AuturaDropdown from 'components/inputs/AuturaDropdown';
import AuturaPhoneField from 'components/inputs/AuturaPhoneField';
import FormFrame from 'components/layout/FormFrame';
import { TIME_ZONES } from 'utils/constants';
import { TenantPanelContext } from './TenantPanel';
import CopyTenantButton from 'components/buttons/CopyTenantButton';
import { getTenantByID } from 'api/tenants';
import { getBlankTenant } from 'utils';

export interface TenantFormLoaderData {
  _tenant: Promise<Tenant> | null;
}

export const tenantFormLoader = ({
  params,
}: LoaderFunctionArgs): TenantFormLoaderData => {
  const targetTenantID = Number(params.targetTenantID);
  if (targetTenantID > 0) {
    return { _tenant: getTenantByID(targetTenantID) };
  }
  return { _tenant: null };
};

export const Component = (): ReactNode => {
  const { t } = useTranslation();
  const { state, dispatch } = useContext(AppContext);
  const { targetTenantID } = useParams();
  const formMode = targetTenantID === '0' ? 'create' : 'edit';
  const { _tenant } = useLoaderData() as TenantFormLoaderData;
  const { tenantGroups, systems, tenants, fetcherKey, onBack } =
    useOutletContext<TenantPanelContext>();
  const fetcher = useFetcher({ key: fetcherKey });
  const [tenant, setTenant] = useState<Tenant>(getBlankTenant());
  const [system, setSystem] = useState<System | null>(null);
  const [tenantGroup, setTenantGroup] = useState<TenantGroup | null>(null);
  const [name, setName] = useState<string>('');
  const [externalID, setExternalID] = useState<string>('');
  const [traxeroID, setTraxeroID] = useState<string>('');
  const [sessionTimeOut, setSessionTimeOut] = useState<number>(0);
  const [copyTenantID, setCopyTenantID] = useState<number>(0);
  const [status, setStatus] = useState<Status>(Status.None);
  const [accountID, setAccountID] = useState<string>('');
  const [contactID, setContactID] = useState<number>(0);
  const [contactEmail, setContactEmail] = useState<string>('');
  const [contactPhone, setContactPhone] = useState<string>('');
  const [contactPhoneType, setContactPhoneType] = useState<PhoneType>(
    PhoneType.None,
  );
  const [contactName, setContactName] = useState<string>('');
  const [timeZone, setTimeZone] = useState<TimeZone>(TIME_ZONES[0]);
  const [allowUserCreationTowerRoles, setAllowUserCreationTowerRoles] =
    useState<boolean>(false);
  const [verifyNumberCharactersVIN, setVerifyNumberCharactersVIN] =
    useState<number>(0);

  useEffect(() => {
    const loadTenant = async () => {
      if (!_tenant) {
        setTenant(getBlankTenant());
        return;
      }
      const loadedTenant = await _tenant;
      if (!loadedTenant) {
        return;
      }
      setTenant(loadedTenant);
      setName(loadedTenant.name);
      setExternalID(loadedTenant.externalID);
      setTraxeroID(loadedTenant.traxeroID);
      setAccountID(loadedTenant.accountID || '');
      setSessionTimeOut(loadedTenant.userSessionTimeOut);
      setStatus(loadedTenant.status);
      setTenantGroup(loadedTenant.tenantGroup || null);
      setContactID(loadedTenant?.contact?.id || 0);
      setContactEmail(loadedTenant?.contact?.primaryEmail || '');
      setContactPhone(loadedTenant?.contact?.primaryPhone || '');
      setContactPhoneType(
        loadedTenant?.contact?.primaryPhoneType || PhoneType.Day,
      );
      setContactName(loadedTenant?.contact?.name || '');
      const matchedTimeZone = TIME_ZONES.find(
        (tz) => tz.label === loadedTenant?.data?.timeZone,
      );
      if (matchedTimeZone) {
        setTimeZone(matchedTimeZone);
      }
      setVerifyNumberCharactersVIN(
        loadedTenant?.data?.verifyNumberCharactersVIN || 0,
      );
      setAllowUserCreationTowerRoles(
        loadedTenant?.data?.allowUserCreationTowerRoles || false,
      );
      setSystem(systems.find((s) => s.id === loadedTenant.systemID) || null);
    };
    loadTenant();
  }, [_tenant, systems]);

  useEffect(() => {
    dispatch({
      type: ACTION_TYPE.SET_HEADER_TEXT,
      payload: `${t('tenant.label', { ns: 'entities' })} ${t('setup')}`,
    });
    dispatch({
      type: ACTION_TYPE.SET_ENTITY_NAME,
      payload: name,
    });
    return () => {
      dispatch({
        type: ACTION_TYPE.SET_ENTITY_NAME,
        payload: '',
      });
    };
  }, [name]);

  // handle the browser back button
  useBackButton(onBack);

  const handleCancelAction = () => {
    onBack();
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    switch (name) {
      case 'name':
        setName(value);
        break;
      case 'externalID':
        setExternalID(value);
        break;
      case 'traxeroID':
        setTraxeroID(value);
        break;
      case 'accountID':
        setAccountID(value);
        break;
      case 'sessionTimeOut':
        setSessionTimeOut(Number(value));
        break;
      case 'contactName':
        setContactName(value);
        break;
      case 'contactEmail':
        setContactEmail(value);
        break;
      case 'verifyNumberCharactersVIN':
        setVerifyNumberCharactersVIN(+value);
        break;
    }
  };

  const handleCopyTenant = (tenant: Tenant) => {
    setCopyTenantID(tenant.id);
  };

  const handleSubmit = () => {
    if (formMode === 'edit') {
      handleUpdate();
    }
    if (formMode === 'create') {
      handleCreate();
    }
  };

  const handleUpdate = () => {
    if (!system) {
      return;
    }
    const updatedTenant: Tenant = {
      ...tenant,
      id: tenant.id,
      name: name,
      systemID: system.id,
      status: status,
      externalID: externalID,
      traxeroID: traxeroID,
      accountID: accountID,
      userSessionTimeOut: sessionTimeOut,
      contact: {
        id: contactID,
        name: contactName,
        primaryPhone: contactPhone,
        primaryPhoneType: contactPhoneType,
        primaryEmail: contactEmail,
        tenantID: tenant.id,
        status: status,
        location: {},
      },
      data: {
        timeZone: timeZone.label,
        verifyNumberCharactersVIN: verifyNumberCharactersVIN,
        allowUserCreationTowerRoles: allowUserCreationTowerRoles,
        entityMerchant: tenant?.data?.entityMerchant || {
          merchantID: '',
          status: Status.Disabled,
          data: {},
        },
        branding: tenant?.data?.branding,
        companyPath: tenant?.data?.companyPath,
      },
    };

    fetcher.submit(
      {
        handler: SuperActionHandler.UPDATE_TENANT,
        tenantID: tenant.id,
        data: JSON.stringify(updatedTenant),
      },
      {
        method: 'PUT',
        action: routeWithParams(ROUTES.COMMAND_CENTER, [state.tenant.id]),
        encType: 'application/json',
      },
    );
  };

  const handleCreate = () => {
    if (!system) {
      return;
    }

    const newTenant: Tenant = {
      id: 0,
      name: name,
      systemID: system.id,
      status: Status.Active,
      systemName: system.name,
      systemData: system.data || {},
      externalID: externalID,
      traxeroID: traxeroID,
      accountID: accountID,
      userSessionTimeOut: sessionTimeOut,
      tenantGroupID: tenantGroup?.id,
      copyTenantID: copyTenantID,
      contact: {
        id: 0,
        name: contactName,
        primaryPhone: contactPhone,
        primaryPhoneType: contactPhoneType,
        primaryEmail: contactEmail,
        status: Status.Active,
        tenantID: 0,
        location: {},
      },
      data: {
        entityMerchant: {
          merchantID: '',
          status: Status.Disabled,
          data: {},
        },
        timeZone: timeZone.timeZone,
        verifyNumberCharactersVIN: verifyNumberCharactersVIN,
        allowUserCreationTowerRoles: allowUserCreationTowerRoles,
        branding: DEFAULT_BRANDING,
        companyPath: '',
      },
    };
    fetcher.submit(
      {
        handler: SuperActionHandler.CREATE_TENANT,
        tenantGroupID: tenantGroup?.id || 0,
        data: JSON.stringify(newTenant),
      },
      {
        method: 'POST',
        action: ROUTES.COMMAND_CENTER,
        encType: 'application/json',
      },
    );
  };

  const title =
    formMode === 'edit'
      ? t('buttons.update', {
          name: t('tenant.label', { ns: 'entities' }),
        })
      : t('buttons.create', {
          name: t('tenant.label', { ns: 'entities' }),
        });

  const tenantGroupOptions: string[] = useMemo(() => {
    return tenantGroups.map((tg) => tg.name);
  }, [tenantGroups]);

  return (
    <ValidatorForm onSubmit={handleSubmit} instantValidate={false}>
      <FormFrame loading={fetcher.state === 'submitting'}>
        <DetailsHeader
          title={title}
          onCancel={handleCancelAction}
          disableSubmit={fetcher.state !== 'idle'}
          submitLabel={title}
          readonly={!state.entityPermissions.tenant.create}
        />
        <ScrollableFormContainer maxHeight={`calc(100dvh - 326px)`}>
          <SettingsSection
            title={t('subheadings.internal_details', { ns: 'commandCenter' })}
          >
            <Grid container width={'100%'} spacing={1.5}>
              <Grid
                size={{
                  xs: 12,
                  lg: 6,
                }}
              >
                <AuturaTextField
                  label={t('tenant.name', { ns: 'entities' })}
                  name='name'
                  value={name}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 6,
                }}
              >
                <AuturaDropdown
                  name='system'
                  label={t('system.label', { ns: 'entities' })}
                  options={['', ...systems.map((s) => s.name)]}
                  value={system?.name || ''}
                  setValue={(e: ChangeEvent<HTMLInputElement>) => {
                    const selectedSystem = systems.find(
                      (s) => s.name === e.target.value,
                    );
                    setSystem(selectedSystem || null);
                  }}
                  required
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 6,
                }}
              >
                <AuturaDropdown
                  required={formMode === 'create'}
                  placeholder={'select a system first'}
                  name='tenantGroupID'
                  label={t('tenant_group.label', { ns: 'entities' })}
                  options={['', ...tenantGroupOptions]}
                  value={tenantGroup?.name || ''}
                  setValue={(e: ChangeEvent<HTMLInputElement>) => {
                    const selectedTenantGroup = tenantGroups.find(
                      (tg: TenantGroup) => tg.name === e.target.value,
                    );
                    setTenantGroup(selectedTenantGroup || null);
                  }}
                  readonly={formMode === 'edit'}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 6,
                }}
              >
                {formMode === 'create' && (
                  <CopyTenantButton
                    onSelect={handleCopyTenant}
                    tenantList={tenants}
                    loading={tenants.length === 0}
                  />
                )}
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  lg: 3,
                }}
              >
                <TimeZoneSelect
                  name='timeZone'
                  inputLabel={`${t('tenant.data.timeZone', {
                    ns: 'entities',
                  })}`}
                  value={timeZone}
                  setValue={(timeZone: TimeZone) => {
                    setTimeZone(timeZone);
                  }}
                  fullWidth
                  required
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  label={t('tenant.externalID', { ns: 'entities' })}
                  name='externalID'
                  value={externalID}
                  onChange={handleChange}
                  readonly={!AuthService.IsSuperAdmin()}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  label={t('tenant.traxeroID', { ns: 'entities' })}
                  name='traxeroID'
                  value={traxeroID}
                  onChange={handleChange}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  label={t('tenant.accountID', { ns: 'entities' })}
                  name='accountID'
                  value={accountID}
                  onChange={handleChange}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 6,
                }}
              >
                <AuturaTextField
                  label={t('tenant.contact.name', { ns: 'entities' })}
                  name='contactName'
                  value={contactName}
                  onChange={handleChange}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  label={t('tenant.contact.primaryEmail', { ns: 'entities' })}
                  name='contactEmail'
                  value={contactEmail}
                  onChange={handleChange}
                  validators={['isEmail']}
                  errorMessages={['Invalid email address']}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaPhoneField
                  label={t('contact.primaryPhone', { ns: 'entities' })}
                  name={'contactPhone'}
                  typeFieldName={'contactPhoneType'}
                  typeFieldLabel={t('tenant.contact.primaryPhoneType', {
                    ns: 'entities',
                  })}
                  phoneNumber={contactPhone}
                  setPhoneNumber={setContactPhone}
                  phoneType={contactPhoneType}
                  setPhoneType={setContactPhoneType}
                />
              </Grid>
            </Grid>
          </SettingsSection>
          <SettingsSection
            title={t('subheadings.tenant_settings', { ns: 'commandCenter' })}
            divider={false}
          >
            <Grid container width={'100%'} spacing={1.5}>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  name='sessionTimeOut'
                  label={t('tenant.userSessionTimeOut', { ns: 'entities' })}
                  type={'number'}
                  value={sessionTimeOut}
                  validators={['minNumber:10']}
                  errorMessages={[
                    t('errors.invalidGreaterThan', {
                      name: t('tenant.sessionTimeout'),
                      value: `${10} ${t('minutes')}`,
                    }),
                  ]}
                  onChange={handleChange}
                  endAdornment={
                    <InputAdornment position='end'>
                      {t('minutes_short')}
                    </InputAdornment>
                  }
                  required
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  label={t('tenant.data.verifyNumberCharactersVIN', {
                    ns: 'entities',
                  })}
                  name='verifyNumberCharactersVIN'
                  value={verifyNumberCharactersVIN}
                  onChange={handleChange}
                  validators={['isNumber']}
                  errorMessages={[t('errors.invalid', { name: t('number') })]}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  lg: 3,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pt: 1.5,
                  }}
                >
                  <AuturaSwitch
                    name='allowUserCreationTowerRoles'
                    label={t('tenant.data.allowUserCreationTowerRoles', {
                      ns: 'entities',
                    })}
                    value={allowUserCreationTowerRoles}
                    setValue={(value: boolean) => {
                      setAllowUserCreationTowerRoles(value);
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </SettingsSection>
        </ScrollableFormContainer>
      </FormFrame>
    </ValidatorForm>
  );
};

Component.displayName = 'TenantForm';
export default Component;
